import { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Button,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react'
import { ArrowsLeftRight } from '@phosphor-icons/react'
import api from '../../services/api'

interface Agent {
  id: number
  name: string
}

export default function TransferTicketModal({
  isOpen,
  onClose,
  ticketId,
  currentAssignedToAgentId,
  onTransferred,
}: {
  isOpen: boolean
  onClose: () => void
  ticketId: number
  currentAssignedToAgentId: number
  onTransferred: () => void
}) {
  const [assignedToAgentId, setAssignedToAgentId] = useState('')
  const [reason, setReason] = useState('')
  const [agents, setAgents] = useState<Agent[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const toast = useToast()
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')

  useEffect(() => {
    if (isOpen) {
      fetchAgents()
      setAssignedToAgentId('')
      setReason('')
    }
  }, [isOpen])

  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents')
      const data = response.data.data || response.data
      setAgents(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching agents:', error)
      toast({ title: 'Erro ao carregar atendentes', status: 'error', duration: 4000 })
    }
  }

  const handleSubmit = async () => {
    if (!assignedToAgentId) {
      toast({ title: 'Selecione o novo destinatário', status: 'warning', duration: 3000 })
      return
    }

    const newId = Number(assignedToAgentId)
    if (newId === currentAssignedToAgentId) {
      toast({ title: 'O novo destinatário deve ser diferente do atual', status: 'warning', duration: 3000 })
      return
    }

    setIsSubmitting(true)
    try {
      await api.post(`/internal-tickets/${ticketId}/transfer`, {
        assignedToAgentId: newId,
        reason: reason.trim() || undefined,
      })
      toast({ title: 'Ticket transferido com sucesso', status: 'success', duration: 3000 })
      onTransferred()
      onClose()
    } catch (error: any) {
      toast({
        title: 'Erro ao transferir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredAgents = agents.filter((a) => a.id !== currentAssignedToAgentId)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">Transferir Ticket</ModalHeader>
        <ModalCloseButton />
        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Novo destinatário</FormLabel>
              <Select
                value={assignedToAgentId}
                onChange={(e) => setAssignedToAgentId(e.target.value)}
                placeholder="Selecione um atendente"
                bg={subtleBg}
                borderColor={borderSubtle}
              >
                {filteredAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Motivo (opcional)</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: melhor conhecimento técnico sobre o assunto"
                bg={subtleBg}
                borderColor={borderSubtle}
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            leftIcon={<ArrowsLeftRight size={18} />}
            bg="yellow.500"
            color="gray.900"
            _hover={{ bg: 'yellow.400' }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            Transferir
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
